import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import { useFeatures } from '../../context/FeatureContext';
import {
  Users,
  User,
  UserCheck,
  GraduationCap,
  Briefcase,
  RotateCcw,
  Shield,
  CheckCircle,
  XCircle,
  Info,
} from 'lucide-react';

// iter335d — role tabs are gated by the tenant plan. Academy roles
// (parent / player / coach / staff) require `enrollments.core`; a
// lane-only booking tenant sees just the Customer role. `null` = always.
const ROLE_FEATURE_GATE = {
  parent: 'enrollments.core',
  coach: 'enrollments.core',
  staff: 'enrollments.core',
  player: 'enrollments.core',
  customer: null,
};

const FeatureSettings = () => {
  const { isPlanFeatureEnabled } = useFeatures();
  const visibleRoles = ['parent', 'coach', 'staff', 'player', 'customer'].filter(
    (r) => isPlanFeatureEnabled(ROLE_FEATURE_GATE[r]),
  );
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [resetDialog, setResetDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(visibleRoles[0] || 'customer');

  useEffect(() => {
    fetchSettings();
  }, []);

  // iter335d — once plan features load, the gated role list can shrink.
  // Reconcile activeTab so a hidden tab (e.g. 'parent') doesn't leave the
  // Tabs with no matching content.
  useEffect(() => {
    if (visibleRoles.length && !visibleRoles.includes(activeTab)) {
      setActiveTab(visibleRoles[0]);
    }
  }, [visibleRoles, activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/features');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  };

  const toggleFeature = async (role, featureKey, currentValue) => {
    const savingKey = `${role}-${featureKey}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    
    try {
      await api.put('/features/toggle', {
        role,
        feature_key: featureKey,
        enabled: !currentValue
      });
      
      // Update local state
      setSettings(prev => ({
        ...prev,
        [role]: {
          ...prev[role],
          [featureKey]: {
            ...prev[role][featureKey],
            enabled: !currentValue
          }
        }
      }));
      
      toast.success(`Feature ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update feature');
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  const resetToDefaults = async () => {
    try {
      await api.post('/features/reset-defaults');
      toast.success('Settings reset to defaults');
      fetchSettings();
      setResetDialog(false);
    } catch (error) {
      toast.error('Failed to reset settings');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'parent': return <Users className="h-5 w-5" />;
      case 'coach': return <GraduationCap className="h-5 w-5" />;
      case 'staff': return <Briefcase className="h-5 w-5" />;
      case 'player': return <User className="h-5 w-5" />;
      case 'customer': return <UserCheck className="h-5 w-5" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'parent': return 'bg-blue-500';
      case 'coach': return 'bg-green-500';
      case 'staff': return 'bg-purple-500';
      case 'player': return 'bg-amber-500';
      case 'customer': return 'bg-orange-500';
      default: return 'bg-slate-500';
    }
  };

  const countEnabledFeatures = (role) => {
    if (!settings || !settings[role]) return { enabled: 0, total: 0 };
    const features = Object.values(settings[role]);
    const enabled = features.filter(f => f.enabled).length;
    return { enabled, total: features.length };
  };

  const renderFeatureCard = (role, featureKey, feature) => {
    const savingKey = `${role}-${featureKey}`;
    const isLoading = saving[savingKey];
    
    return (
      <div 
        key={featureKey}
        className={`p-4 rounded-lg border transition-all ${
          feature.enabled 
            ? 'bg-white border-slate-200' 
            : 'bg-slate-50 border-slate-100'
        }`}
        data-testid={`feature-${role}-${featureKey}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-medium ${feature.enabled ? 'text-slate-900' : 'text-slate-500'}`}>
                {feature.label}
              </h4>
              {feature.enabled ? (
                <Badge className="bg-green-100 text-green-700 text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 text-xs">
                  <XCircle className="h-3 w-3 mr-1" />
                  Disabled
                </Badge>
              )}
            </div>
            <p className={`text-sm ${feature.enabled ? 'text-slate-600' : 'text-slate-400'}`}>
              {feature.description}
            </p>
          </div>
          <Switch
            checked={feature.enabled}
            onCheckedChange={() => toggleFeature(role, featureKey, feature.enabled)}
            disabled={isLoading}
            data-testid={`toggle-${role}-${featureKey}`}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Feature Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Feature Settings">
      <div className="space-y-6" data-testid="feature-settings-page">
        {/* Header */}
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="h-7 w-7 text-blue-600" />
              Feature Settings
            </h1>
            <p className="text-slate-500 mt-1">
              Enable or disable features for each user role
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setResetDialog(true)}
            className="text-slate-600"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800">
                  <strong>How it works:</strong> Disabled features will be hidden from users of that role. 
                  They won&apos;t see the menu items or have access to those pages.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Role Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          {visibleRoles.map(role => {
            const counts = countEnabledFeatures(role);
            return (
              <Card 
                key={role}
                className={`cursor-pointer transition-all ${activeTab === role ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => setActiveTab(role)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-full ${getRoleColor(role)} bg-opacity-10`}>
                        {getRoleIcon(role)}
                      </div>
                      <div>
                        <h3 className="font-semibold capitalize text-slate-900">{role}</h3>
                        <p className="text-sm text-slate-500">
                          {counts.enabled} of {counts.total} features enabled
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${counts.enabled === counts.total ? 'text-green-600' : 'text-slate-600'}`}>
                        {Math.round((counts.enabled / counts.total) * 100)}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feature Toggles */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex w-full max-w-2xl overflow-x-auto">
            {visibleRoles.map(role => (
              <TabsTrigger
                key={role}
                value={role}
                className="flex items-center gap-2 capitalize"
                data-testid={`feature-role-tab-${role}`}
              >
                {getRoleIcon(role)}
                {role}
              </TabsTrigger>
            ))}
          </TabsList>

          {visibleRoles.map(role => (
            <TabsContent key={role} value={role} className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 capitalize">
                    {getRoleIcon(role)}
                    {role} Features
                  </CardTitle>
                  <CardDescription>
                    Control which features are available to {role}s
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {settings && settings[role] && 
                      Object.entries(settings[role]).map(([key, feature]) => 
                        renderFeatureCard(role, key, feature)
                      )
                    }
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Reset Confirmation Dialog */}
        <AlertDialog open={resetDialog} onOpenChange={setResetDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Reset to Default Settings?</AlertDialogTitle>
              <AlertDialogDescription>
                This will reset all feature settings to their default values. 
                Some features that you&apos;ve disabled may be re-enabled.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={resetToDefaults}>
                Reset to Defaults
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default FeatureSettings;
